"use strict";

const _ = require("lodash");
const GridCell = require("../../cell/GridCellComponent");
const GridRowRenderer = require("../GridRowRenderer");
const GroupingColumnRendererComponent = require("../../column_renderer/grouping/GroupingColumnComponent");
const EmptyColumnRendererComponent = require("../../column_renderer/empty/EmptyColumnComponent");

class LockedGridGroupRowRenderer extends GridRowRenderer {
    constructor(model) {
        super(model);

        this._registerListeners();
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _createRootElement() {
        const rootElem = document.createElement("locked-grid-row");
        rootElem.classList.add("group-header-row");
        return rootElem;
    }

    _registerListeners() {
        this._model.on("Expand", (this._onExpand = this._onExpand.bind(this)));
        return super._registerListeners();
    }

    _unregisterListeners() {
        this._model.removeListener("Expand", this._onExpand);
        return super._unregisterListeners();
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._setExpanded(this._model.getExpanded());
        return super.render();
    }

    shutdown() {
        return super.shutdown();
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _onExpand(state) {
        this._setExpanded(state);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _getColumns() {
        return this._model.getLockedColumns();
    }

    /**
     * Build the row display from the entity
     * @param entity {object} New entity that contains data for the current row
     * @private
     */
    _setEntity(entity) {
        for (let cell in this._cells) {
            if (this._cells.hasOwnProperty(cell)) {
                this._cells[cell].removeAllListeners("CellAction");
                this._cells[cell].shutdown();
            }
        }
        this._cells = {};
        let columns = this._getColumns(),
            columnComponents = this._model.getColumnComponents();
        columns.forEach((columnKey) => {
            const colComponent = columnComponents[columnKey];
            const getRenderer = (colComponent) => {
                if (!colComponent) {
                    return undefined;
                }
                if (colComponent.isGroupingColumn) {
                    return GroupingColumnRendererComponent;
                }
                if (colComponent.isGroupOnlyColumn || colComponent.isCombinedColumn) {
                    return (
                        colComponent.getColumnRendererComponent() || EmptyColumnRendererComponent
                    );
                }
                return EmptyColumnRendererComponent;
            };

            let cell = new GridCell({
                id: columnKey,
                column: colComponent,
                columnRendererComponent: getRenderer(colComponent),
                entityColumn: colComponent ? colComponent.getEntityColumn() : undefined,
                entity: entity,
                context: colComponent ? colComponent.getContext() : undefined,
                path: colComponent.path || "count",
                shown: colComponent ? colComponent.getShown() : undefined
            });
            cell.addListener("CellAction", (this._onCellAction = this._onCellAction.bind(this)));
            this._rootElem.appendChild(cell.renderer.rootElement);
            this._cells[columnKey] = cell;
        });
    }
    /**
     * Rebuild the row with the new column order
     * @param lockedColumns {Array} - Array of Strings (column ids) representing the order of the locked columns
     * @private
     */
    _setLockedColumns(lockedColumns) {
        // console.info('SET LOCKED COLUMNS');
        let columnComponents = this._model.getColumnComponents(),
            cellKeys = Object.keys(this._cells),
            cellsToRemove = _.difference(cellKeys, lockedColumns),
            cellsToAdd = _.difference(lockedColumns, cellKeys),
            entity = this._model.getEntity();
        cellsToRemove.forEach((columnKey) => {
            this._cells[columnKey].removeAllListeners("CellAction");
            this._cells[columnKey].shutdown();
            delete this._cells[columnKey];
        });
        cellsToAdd.forEach((columnKey) => {
            const colComponent = columnComponents[columnKey];
            let nextCellIndex = lockedColumns.indexOf(columnKey) + 1,
                /* jshint -W014 */
                nextCellKey =
                    nextCellIndex <= lockedColumns.length - 1
                        ? lockedColumns[nextCellIndex]
                        : undefined,
                /* jshint -W014 */
                nextCellRenderer =
                    !_.isUndefined(nextCellKey) && this._cells[nextCellKey]
                        ? this._cells[nextCellKey].renderer.rootElement
                        : null;

            let cell = new GridCell({
                id: columnKey,
                column: colComponent,
                /* jshint -W014 */
                columnRendererComponent: colComponent.isGroupingColumn
                    ? GroupingColumnRendererComponent
                    : EmptyColumnRendererComponent,
                /* jshint -W014 */
                entityColumn: colComponent.isGroupingColumn
                    ? colComponent.getEntityColumn()
                    : false,
                entity: entity,
                context: colComponent.getContext(),
                path: "count",
                shown: colComponent.getShown()
            });
            cell.addListener("CellAction", (this._onCellAction = this._onCellAction.bind(this)));
            this._cells[columnKey] = cell;
            this._rootElem.insertBefore(cell.renderer.rootElement, nextCellRenderer);
        });
    }

    _setExpanded(state) {
        this._rootElem.classList.toggle("expanded", state);
        this._rootElem.classList.toggle("collapsed", !state);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = LockedGridGroupRowRenderer;
